import { color, Icon, typography } from '@primer-io/goat'
import { ExternalLink } from '@primer-io/goat-icons'
import ArrowLink from 'components/ArrowLink'
import * as ArticleComponents from 'components/ArticleComponents'
import Clickable from 'components/Clickable'
import Box from 'ds/Box'
import Grid from 'ds/Grid'
import * as TextGoat from 'ds/TextGoat'
import React from 'react'
import styled from 'styled-components'

export const Header = styled(TextGoat.H2)`
	margin-bottom: 24px;

	${p => p.theme.above('medium')`
		margin-bottom: 40px;
  `}
`

export const Subheader = styled(ArticleComponents.P)`
	margin-top: 8px;
	max-width: 450px;
	font-weight: 300;
	color: ${color('gray-800')};
`

const LearnMoreButtonWrapper = styled.div`
	position: relative;
	left: -10px;

	& .goat-Button_ghost:hover {
		background-color: rgba(0, 0, 0, 0.05);
	}
`

export const LearnMoreButton = props => (
	<LearnMoreButtonWrapper>
		<ArrowLink icon='ArrowRight' {...props} />
	</LearnMoreButtonWrapper>
)

///////////////////////////////////////////
// Section Banner
///////////////////////////////////////////

const SectionBannerWrapper = styled.div`
	display: flex;
	min-height: 300px;
	border-radius: 4px;
	background: ${color('yellow-200')};
	flex-direction: column-reverse;

	${p => p.theme.above('medium')`
		flex-direction: row;
  `}
`

const SectionBannerContentWrapper = styled.div`
	display: flex;
	flex: 1;
	align-items: center;
	padding: 24px 24px;
`

const SectionBannerHeader = styled.div`
	${typography('title-large')};
	${p => p.theme.above('medium')`
		${typography('header-small')};
	`}
	margin-bottom: 8px;
`

const SectionBannerDescription = styled.div`
	${typography('body-medium')};
	${p => p.theme.above('medium')`
		${typography('body-large')};
	`}
	margin-bottom: 24px;
`

const SectionBannerImgContainer = styled.div`
	flex: 1;
	position: relative;
	min-height: 200px;
`

const SectionBannerImg = styled.img`
	position: absolute;
	top: 0px;
	left: 0px;
	width: 100%;
	height: 100%;

	object-fit: contain;
	object-position: bottom right;

	${p => p.theme.above('medium')`
		object-fit: cover;
		object-position: left bottom;
  `}
`

export const SectionBanner = ({ title, description, backgroundColor, imgSrc, to }) => {
	return (
		<SectionBannerWrapper style={{ background: backgroundColor }}>
			<SectionBannerContentWrapper>
				<div>
					<SectionBannerHeader>{title}</SectionBannerHeader>
					<SectionBannerDescription>{description}</SectionBannerDescription>

					<LearnMoreButton label='Learn more' to={to} />
				</div>
			</SectionBannerContentWrapper>
			<SectionBannerImgContainer>
				<SectionBannerImg src={imgSrc} />
			</SectionBannerImgContainer>
		</SectionBannerWrapper>
	)
}

///////////////////////////////////////////
// Guides
///////////////////////////////////////////

export const P = styled.p`
	${typography('body-small')}
	color: ${color('gray-700')};
`

const Cover = styled.div`
	display: ${p => (p.hideWhenSmall ? 'none' : 'block')};
	${p => p.theme.above('medium')`
		display: block;
	`}

	aspect-ratio: ${p => p.aspectRatio ?? 3};
	margin: -20px -20px 4px;
	background: ${color('gray-200')} center / cover url(${p => p.imgSrc});
`
const GuideLinkTitleContainer = styled.div`
	display: flex;
	align-items: center;
`

const GuideLinkTitle = styled.h4`
	${typography('title-large')}
	color: ${color('gray-900')};
	flex: 1;
`

const StyledBox = styled(Box).attrs(() => ({
	gap: 12,
	padding: 20,
	justify: 'flex-start',
	style: {
		textDecoration: 'none',
	},
}))`
	& > p {
		color: ${color('gray-700')};
	}
`

const Tag = styled.div`
	${typography('body-small')}
	color: ${color('gray-900')};
	padding: 2px;
	border-radius: 4px;
	background: ${color('gray-100')};

	margin-left: 8px;
`

export const GuideLink = ({
	title,
	description,
	link,
	imgSrc,
	icon,
	tag,
	coverAspectRatio,
	hideCoverWhenSmall,
	...rest
}) => (
	<StyledBox as={Clickable} to={link} {...rest}>
		{imgSrc && <Cover imgSrc={imgSrc} aspectRatio={coverAspectRatio} hideWhenSmall={hideCoverWhenSmall} />}
		<GuideLinkTitleContainer>
			<GuideLinkTitle>{title}</GuideLinkTitle>
			{tag && <Tag>{tag}</Tag>}
			{icon && <Icon as={icon} color='gray-900' size='small' />}
		</GuideLinkTitleContainer>
		<P>{description}</P>
	</StyledBox>
)

///////////////////////////////////////////
// USP
///////////////////////////////////////////

export const USPsWrapper = styled(Grid).attrs(() => ({
	cols: { sm: 2, md: 4 },
}))`
	place-items: top;

	padding: 24px 0px;
`

export const USP = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;

	max-width: 180px;
	${typography('body-medium')}
	color: ${color('gray-900')};
	text-align: center;

	svg {
		margin-bottom: 8px;
		width: 32px;
		height: 32px;
	}
`
